import React, { useContext, useEffect } from "react";
import { useState } from "react";
import {
  DatePicker,
  Form,
  Input,
  Select,
  Button,
  Popconfirm,
  message,
} from "antd";
import PhoneInput from "react-phone-input-2";
import { CameraOutlined, EyeOutlined } from "@ant-design/icons";
import profile from "../../../Assets/Icons/risk-profile-icon.svg";
import header from "../../../Assets/Icons/starHeader.svg";
import editIcon from "../../../Assets/Icons/edit.svg";
import logoutIcon from "../../../Assets/Icons/logoutsetting.svg";
import customerLogo from "../../../Assets/Icons/customer manage.svg";
import GenericModal from "../../../Components/GenericModal";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../App";
import {
  handleLogout,
  handleUpdateUser,
} from "../../../Utils/UpdateUsersState";
import { settingUser } from "../../../API/Admin/user";
import "./style.css";

const Settings = () => {
  const { userData, setUserData, setUserToken } = useContext(UserContext);
  const [messageApi, contextHolder] = message.useMessage();

  const [isDisabled, setIsDisabled] = useState(true);
  const [saved, setSaved] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [user, setUser] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const handleEdit = () => {
    setIsDisabled((prev) => !prev);
    setMode("edit");
  };

  const handleCancel = () => {
    form.resetFields();
    setSaved(false);
    message.info("Changes canceled");
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const formData = new FormData();

      formData.append("user_id", userData?.id);
      formData.append("fullName", values.fullName);
      formData.append("email", values.email);
      formData.append("phone", values.phone);

      if (values.password) {
        formData.append("password", values.password);
      }

      if (values.confirmPassword) {
        formData.append("confirmPassword", values.confirmPassword);
      }

      if (selectedImage) {
        formData.append("profile_picture", selectedImage);
      }

      const response = await settingUser(formData);

      if (response?.status >= 200 && response?.status < 300) {
        messageApi.success(
          response?.data?.message || "User updated successfully!",
        );

        const updatedUser = {
          ...userData,
          name: response?.data?.user?.name || values.fullName,
          email: response?.data?.user?.email || values.email,
          phone: response?.data?.user?.phone || values.phone,
          role: response?.data?.user?.role || userData?.role,
          profile_picture: response?.data?.user?.profile_picture || userData?.profile_picture,
        };

        setUserData(updatedUser);
        setUser(updatedUser);
        setIsDisabled(true);
        setMode(null);

        form.setFieldsValue({
          password: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      if (error?.errorFields) return;

      let errorMessage = "Something went wrong.";

      if (error?.response) {
        const data = error.response.data;

        if (typeof data === "string") errorMessage = data;
        else if (data?.message) errorMessage = data.message;
        else if (data?.error) errorMessage = data.error;
        else if (data?.detail) errorMessage = data.detail;
        else if (typeof data === "object") {
          const firstKey = Object.keys(data)[0];
          const msg = Array.isArray(data[firstKey])
            ? data[firstKey][0]
            : data[firstKey];

          errorMessage = msg;
        }
      }

      messageApi.error(errorMessage);
      console.error("Submission Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    setIsOpen(false);
    handleLogout(setUserData, setUserToken);
    messageApi.success("Logged Out");
    navigate("/login");
  };

  const handleDiscard = () => {
    setIsOpen(false);
  };
  const showLogoutModal = () => {
    setIsOpen(true);
  };

  return (
    <div className="settings">
      {contextHolder}
      <div className="header">
        <img src={header} alt="Decoration" className="header-img" />
      </div>
      <div className="settings-container">
        <div className="profile-details">
          <div className="image">
            <img
              src={
                previewImage
                  ? previewImage
                  : userData?.profile_picture
                    ? `${process.env.REACT_APP_MEDIA_URL}${userData.profile_picture}`
                    : customerLogo
              }
              alt="Profile Icon"
              className="profile-image"
            />

            <input
              type="file"
              id="profile-upload"
              hidden
              accept="image/*"
              disabled={isDisabled}
              onChange={(e) => {
                const file = e.target.files[0];

                if (file) {
                  setSelectedImage(file);
                  setPreviewImage(URL.createObjectURL(file));
                }
              }}
            />

            <button
              type="button"
              className={`camera-btn ${isDisabled ? "disabled" : "enabled"}`}
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  document.getElementById("profile-upload").click();
                }
              }}
            >
              <CameraOutlined />
            </button>
          </div>
          <div className="profile-wrapper">
            <div className="info">
              <div className="name">{userData?.name}</div>
              <div className="email">{userData?.email}</div>
            </div>
            <Button type="default" onClick={handleEdit} className="edit-btn">
              <img
                src={isDisabled ? editIcon : undefined}
                alt={isDisabled ? "edit user" : "view user"}
                className="btn-icon"
                style={{ display: isDisabled ? "inline" : "none" }}
              />
              {!isDisabled && <EyeOutlined />}
              {isDisabled ? "Edit" : "View"}
            </Button>
          </div>
        </div>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            fullName: userData?.name,
            email: userData?.email,
            phone: userData?.phone,
          }}
        >
          <div className="heading">Admin Information</div>
          <div className="details">
            <div className="input-group">
              <div className="title">Full Name</div>
              <Form.Item name="fullName">
                <Input placeholder="Enter name" disabled={isDisabled} />
              </Form.Item>
            </div>
            <div className="input-group">
              <div className="title">Email Address</div>
              <Form.Item
                name="email"
                rules={[
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input placeholder="Enter email" disabled={isDisabled} />
              </Form.Item>
            </div>
            <div className="input-group">
              <div className="title">Phone Number</div>
              <Form.Item name="phone">
                <PhoneInput
                  country={"jm"}
                  placeholder="Enter phone number"
                  inputClass={`phone-input ${isDisabled ? "disabled" : ""}`}
                  specialLabel={false}
                  disabled={isDisabled}
                  onChange={(value) => {
                    form.setFieldsValue({
                      phone: value.startsWith("+") ? value : `+${value}`,
                    });
                  }}
                />
              </Form.Item>
            </div>
          </div>

          <div className="heading">Password</div>
          <div className="details">
            <div className="input-group">
              <div className="title">Password</div>
              <Form.Item
                name="password"
                rules={[
                  {
                    min: 6,
                    message: "Password must be at least 6 characters!",
                  },
                ]}
              >
                <Input.Password
                  placeholder="Enter password"
                  disabled={isDisabled}
                />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Confirm Password</div>
              <Form.Item
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match!"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm password"
                  disabled={isDisabled}
                />
              </Form.Item>
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="logout-btn">
              <Button onClick={showLogoutModal} className="btn-logout">
                <img src={logoutIcon} alt="Logout" />
                Logout
              </Button>
            </div>

            <div className="setting-modal">
              <GenericModal
                title="Log out"
                open={isOpen}
                onOk={handleOk}
                onCancel={handleDiscard}
                okText="Yes Sure"
                cancelText="Cancel"
                cancelStyle={true}
              >
                <p>Are you sure you really want to log out?</p>
              </GenericModal>
            </div>

            <div className="form-actions">
              <Button disabled={isDisabled} onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                disabled={isDisabled}
                onClick={handleSubmit}
                className="save-user"
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Settings;
